import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { Download, Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { inventarioApi } from '@/services/inventarioApi';
import type { ImportResultDto } from '@/types/inventario';

const TEMPLATE_FILENAME = 'plantilla-inventario.xlsx';

export function ImportarExcelView() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [result, setResult] = useState<ImportResultDto | null>(null);
  const [downloading, setDownloading] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: (f: File) => inventarioApi.uploadFile(f),
    onSuccess: (res) => {
      setResult(res);
      queryClient.invalidateQueries({ queryKey: ['inventario-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['inventario-stock'] });
      queryClient.invalidateQueries({ queryKey: ['movimientos'] });
      queryClient.invalidateQueries({ queryKey: ['kardex'] });
      queryClient.invalidateQueries({ queryKey: ['otros-items'] });
      queryClient.invalidateQueries({ queryKey: ['inventario-disponibilidad'] });
      if (res.filasFallidas === 0) {
        toast.success(`Importadas ${res.filasExitosas} filas`);
      } else {
        toast.warning(`${res.filasExitosas} filas OK · ${res.filasFallidas} con errores`);
      }
    },
    onError: (err: unknown) => {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error ?? 'Error al importar archivo'
        : 'Error al importar archivo';
      toast.error(msg);
    },
  });

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const blob = await inventarioApi.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = TEMPLATE_FILENAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar la plantilla');
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = (f: File | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Solo se permiten archivos .xlsx');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleSubmit = () => {
    if (!file) { toast.error('Selecciona un archivo'); return; }
    uploadMutation.mutate(file);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-1">Plantilla Excel</h3>
        <p className="text-sm text-gray-500 mb-4">
          Descarga la plantilla con instrucciones, hoja de movimientos masivos, hoja para crear ítems "otros" y referencia de almacenes.
        </p>
        <button
          onClick={handleDownloadTemplate}
          disabled={downloading}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          <Download size={16} />
          {downloading ? 'Descargando…' : 'Descargar plantilla'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-1">Subir archivo</h3>
        <p className="text-sm text-gray-500 mb-4">
          Cada fila se procesa de forma independiente: si una falla, las demás se importan igual. Las hojas se procesan en orden: <strong>NUEVOS_OTROS</strong> primero, luego <strong>MOVIMIENTOS</strong>.
        </p>

        <div
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            handleFile(e.dataTransfer.files?.[0] ?? null);
          }}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer transition-colors ${
            dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
          }`}
        >
          <FileSpreadsheet size={32} className={dragActive ? 'text-blue-500' : 'text-gray-300'} />
          {file ? (
            <>
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); reset(); }}
                className="text-xs text-blue-600 hover:underline mt-1"
              >
                Cambiar archivo
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-700">Arrastra el .xlsx o haz clic para seleccionar</p>
              <p className="text-xs text-gray-400">Solo archivos Excel (.xlsx)</p>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
        />

        <div className="flex justify-end gap-3 mt-4">
          {file && (
            <button
              onClick={reset}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Cancelar
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={!file || uploadMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <Upload size={16} />
            {uploadMutation.isPending ? 'Procesando…' : 'Importar'}
          </button>
        </div>
      </div>

      {result && <ImportResultPanel result={result} />}
    </div>
  );
}

function ImportResultPanel({ result }: { result: ImportResultDto }) {
  const allOk = result.filasFallidas === 0;
  const ringClass = allOk ? 'border-emerald-200' : 'border-amber-200';

  return (
    <div className={`bg-white rounded-xl border ${ringClass} p-4 sm:p-6 space-y-4`}>
      <div className="flex items-center gap-3">
        {allOk ? (
          <CheckCircle2 className="text-emerald-500" size={28} />
        ) : (
          <AlertTriangle className="text-amber-500" size={28} />
        )}
        <div>
          <h3 className="font-semibold text-gray-900">
            {allOk ? 'Importación exitosa' : 'Importación con errores'}
          </h3>
          <p className="text-xs text-gray-500">
            Procesado el {new Date(result.timestamp).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Metric label="Total filas" value={result.totalFilas} />
        <Metric label="Exitosas" value={result.filasExitosas} tone="emerald" />
        <Metric label="Fallidas" value={result.filasFallidas} tone={result.filasFallidas > 0 ? 'red' : 'gray'} />
        <Metric label="Ítems creados" value={result.nuevosItemsCreados} />
        <Metric label="Movimientos" value={result.movimientosRegistrados} />
      </div>

      {result.errores.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <XCircle size={16} className="text-red-500" />
            Errores ({result.errores.length})
          </h4>
          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="border-b border-gray-200">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Hoja</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Fila</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Columna</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Mensaje</th>
                </tr>
              </thead>
              <tbody>
                {result.errores.map((e, idx) => (
                  <tr key={idx} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 text-xs font-mono text-gray-600">{e.sheet}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{e.row}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{e.columna ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-red-600">{e.mensaje}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone = 'gray' }: { label: string; value: number; tone?: 'gray' | 'emerald' | 'red' }) {
  const palette = {
    gray: 'text-gray-900',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
  }[tone];
  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-xl font-bold ${palette}`}>{value}</p>
    </div>
  );
}
